/**
 * Límites mensuales de facturación por emisor (billing_entities.monthly_limits).
 *
 * `monthly_limits` es un jsonb con claves `YYYY-MM` → monto tope de ese mes.
 * Existe además `monthly_limit` (columna suelta, legado) que actúa como tope
 * general cuando el mes no tiene clave propia: se conserva para no romper
 * emisores viejos, pero NO alcanza para el lógico
 * `organizations.require_billing_entity_month_limit`.
 *
 * Ese lógico existe porque el tope mensual es una decisión que hay que tomar mes
 * a mes (categoría de monotributo, cupo acordado con el contador). Un emisor sin
 * tope cargado factura ilimitado sin que nadie se entere; con el lógico
 * encendido, facturar sin haber cargado el tope del mes en curso se bloquea.
 *
 * La lógica vive acá y no dentro de Facturacion.jsx para poder testearla sin
 * montar la pantalla.
 */

/** Clave `YYYY-MM` del mes, en hora LOCAL (no UTC: el 31 a la noche en AR aún es el mismo mes). */
export const buildMonthKey = (date = new Date()) => {
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const isMonthKey = (value) => /^\d{4}-\d{2}$/.test(String(value || '').trim());

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Monto cargado para ese mes puntual (ignora el legacy). 0 si no hay. */
export const getMonthOnlyLimit = (entity, monthKey) => {
  const key = String(monthKey || '').trim();
  const map = entity?.monthly_limits;
  if (!key || !map || typeof map !== 'object') return 0;
  if (!Object.prototype.hasOwnProperty.call(map, key)) return 0;
  return toNumber(map[key]);
};

/**
 * Tope vigente para el mes: la clave del mes manda; si no hay, cae al legacy.
 * Devuelve 0 cuando no hay tope (= ilimitado, salvo que el lógico lo exija).
 */
export const getEntityMonthLimit = (entity, monthKey) => {
  const delMes = getMonthOnlyLimit(entity, monthKey);
  if (delMes > 0) return delMes;
  const legacy = toNumber(entity?.monthly_limit);
  return legacy > 0 ? legacy : 0;
};

/**
 * ¿El emisor tiene tope cargado para ESE mes? Requiere la clave `YYYY-MM` con
 * monto mayor a cero. El legacy no cuenta a propósito.
 */
export const hasMonthLimitConfigured = (entity, monthKey) =>
  isMonthKey(monthKey) && getMonthOnlyLimit(entity, monthKey) > 0;

/** Consumido del tope; nunca negativo. */
export const getEntityRemaining = (entity, monthKey, spent = 0) => {
  const limit = getEntityMonthLimit(entity, monthKey);
  if (limit <= 0) return Infinity;
  return Math.max(0, limit - toNumber(spent));
};

/**
 * Validación única para facturar con un emisor.
 *
 * @param {object}  p
 * @param {object?} p.entity            fila de billing_entities
 * @param {string}  p.monthKey          mes en curso (`YYYY-MM`)
 * @param {number}  p.spent             facturado del mes por ese emisor
 * @param {number}  p.amount            importe de la venta a facturar
 * @param {boolean} p.requireMonthLimit lógico de la organización
 * @returns {{ok:boolean, code:null|'SIN_EMISOR'|'EMISOR_INACTIVO'|'SIN_LIMITE_MES'|'LIMITE_EXCEDIDO',
 *            monthKey:string, limit:number, spent:number, remaining:number, amount:number}}
 */
export const validateBillingEntityForInvoice = ({
  entity = null,
  monthKey = buildMonthKey(),
  spent = 0,
  amount = 0,
  requireMonthLimit = false,
} = {}) => {
  const limit = getEntityMonthLimit(entity, monthKey);
  const gastado = toNumber(spent);
  const remaining = limit > 0 ? Math.max(0, limit - gastado) : Infinity;
  const monto = Math.abs(toNumber(amount));
  const base = { monthKey, limit, spent: gastado, remaining, amount: monto };

  if (!entity) return { ok: false, code: 'SIN_EMISOR', ...base };
  if (entity.is_active === false) return { ok: false, code: 'EMISOR_INACTIVO', ...base };

  // El control del lógico va ANTES del monto: la razón del bloqueo es que falta
  // configurar el tope, y una venta en cero no puede saltearlo.
  if (requireMonthLimit && !hasMonthLimitConfigured(entity, monthKey)) {
    return { ok: false, code: 'SIN_LIMITE_MES', ...base };
  }

  if (limit > 0 && monto > 0 && remaining < monto) {
    return { ok: false, code: 'LIMITE_EXCEDIDO', ...base };
  }

  return { ok: true, code: null, ...base };
};
